import { useStaff, useThreadMetrics } from '@zuruflex/api-client/react';
import { formatNumberIn, intlTagFor, toLocale, type Locale } from '@zuruflex/i18n';
import { CSAT_SCORES, type ThreadMetricsBucket } from '@zuruflex/types';
import {
  BarList,
  Card,
  DataTable,
  DateRangePicker,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  StatTile,
  type ColumnDef,
} from '@zuruflex/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

/**
 * How the desk is doing, over a window somebody chose.
 *
 * At `/reports` rather than `/inbox/reports`, which would collide with
 * `/inbox/:id` — the router would read "reports" as a thread id and the
 * page would render a 404 for a conversation nobody asked for.
 *
 * Every figure comes from `GET /v1/threads/metrics`, percentiles and all.
 * They are computed there over every thread in the range rather than here
 * over the page of rows a dashboard happens to be holding, which is the
 * difference between a median and a rumour.
 */

/** A count of minutes or hours, in the reader's language. `Intl` knows the unit names. */
function inUnit(value: number | null, unit: 'minute' | 'hour', locale: Locale): string {
  if (value === null) return '—';
  return new Intl.NumberFormat(intlTagFor(locale), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
}

/** Midnight at the start, the last instant at the end — a day is a whole day. */
function startOf(date: Date): string {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next.toISOString();
}

function endOf(date: Date): string {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next.toISOString();
}

interface AgentRow {
  staffId: string;
  fullName: string;
  assigned: number;
  resolved: number;
  firstResponseMinutesP50: number | null;
}

export function ReportsPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const [range, setRange] = useState<DateRange>();

  /*
   * Both ends or neither. A half-picked range would ask the API for
   * everything since a date and get the default thirty days back, which is
   * a report that quietly disagrees with the control above it.
   */
  const window = useMemo(
    () =>
      range?.from && range.to ? { from: startOf(range.from), to: endOf(range.to) } : {},
    [range],
  );

  const metrics = useThreadMetrics(api, window);
  // Inactive colleagues included: somebody who left in March is still all
  // over March's figures and must still be readable by name.
  const staff = useStaff(api, { includeInactive: true });

  const columns = useMemo<ColumnDef<AgentRow>[]>(
    () => [
      { accessorKey: 'fullName', header: t('reports.agent') },
      { accessorKey: 'assigned', header: t('reports.assigned') },
      { accessorKey: 'resolved', header: t('reports.resolved') },
      {
        accessorKey: 'firstResponseMinutesP50',
        header: t('reports.firstResponse'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {inUnit(row.original.firstResponseMinutesP50, 'minute', locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const bars = (
    buckets: ThreadMetricsBucket[],
    label: (key: string) => string,
    link?: (key: string) => string,
  ) =>
    buckets.map((bucket) => ({
      key: bucket.key,
      label: label(bucket.key),
      value: bucket.count,
      ...(link ? { to: link(bucket.key) } : {}),
    }));

  const data = metrics.data;
  const empty = data ? data.opened === 0 && data.resolved === 0 : false;

  return (
    <>
      <PageHeader
        title={t('reports.title')}
        description={t('reports.subtitle')}
        action={
          <DateRangePicker
            value={range}
            onChange={setRange}
            // A report looks backwards; a picker that refuses yesterday is
            // one that can only report on a range nothing has happened in.
            disablePast={false}
            placeholder={t('reports.range')}
          />
        }
      />

      {metrics.error ? (
        <ErrorState error={metrics.error} onRetry={() => void metrics.refetch()} />
      ) : metrics.isPending || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : empty ? (
        <EmptyState title={t('reports.empty')} />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label={t('reports.opened')}
              value={formatNumberIn(data.opened, locale)}
              accent
              to="/inbox"
            />
            <StatTile
              label={t('reports.resolved')}
              value={formatNumberIn(data.resolved, locale)}
            />
            <StatTile
              label={t('reports.firstResponse')}
              value={inUnit(data.firstResponseMinutes.p50, 'minute', locale)}
              hint={`${t('reports.p90')} ${inUnit(data.firstResponseMinutes.p90, 'minute', locale)}`}
            />
            <StatTile
              label={t('reports.resolution')}
              value={inUnit(data.resolutionHours.p50, 'hour', locale)}
              hint={`${t('reports.p90')} ${inUnit(data.resolutionHours.p90, 'hour', locale)}`}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {/*
              Carried beside the percentiles precisely because it is missing
              from them: a desk that answers three threads in a minute and
              leaves thirty untouched reports a superb median, and only this
              number contradicts it. It links into the view that fixes it —
              the threads whose first-reply clock is running out.
            */}
            <StatTile
              label={t('reports.unanswered')}
              value={formatNumberIn(data.firstResponseMinutes.unanswered, locale)}
              to="/inbox?view=breaching"
            />
            <StatTile
              label={t('reports.csat')}
              value={
                data.csat.average === null
                  ? '—'
                  : /*
                      The average out of five, in figures. It used to carry a
                      row of stars beside the number, and stars are this
                      product's mark for rating a *car* — the question CSAT
                      asks is a different one and wears a different mark
                      everywhere it is drawn. See `CsatScoreMark`.
                    */
                    `${formatNumberIn(Math.round(data.csat.average * 10) / 10, locale)} / ${CSAT_SCORES.length}`
              }
              // Answers and not asks: a dozen scores out of four hundred
              // threads says the prompt is being ignored, not that the desk
              // is unpopular.
              hint={t('reports.csatCount', { count: data.csat.count })}
            />
            <StatTile
              label={t('reports.byStatus')}
              value={formatNumberIn(
                data.byStatus.reduce((sum, bucket) => sum + bucket.count, 0),
                locale,
              )}
              footer={
                <BarList
                  items={bars(
                    data.byStatus,
                    (key) => t(`inbox.status.${key}`),
                    (key) => `/inbox?status=${key}`,
                  )}
                  format={(value) => formatNumberIn(value, locale)}
                />
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="p-5">
              <h2 className="font-display text-lg font-bold">{t('reports.byTopic')}</h2>
              <BarList
                className="mt-3"
                items={bars(
                  data.byTopic,
                  (key) => t(`inbox.topic.${key}`),
                  (key) => `/inbox?topic=${key}`,
                )}
                format={(value) => formatNumberIn(value, locale)}
              />
            </Card>

            <Card className="p-5">
              <h2 className="font-display text-lg font-bold">{t('reports.byChannel')}</h2>
              <BarList
                className="mt-3"
                items={bars(
                  data.byChannel,
                  (key) => t(`inbox.channel.${key}`),
                  (key) => `/inbox?channel=${key}`,
                )}
                format={(value) => formatNumberIn(value, locale)}
              />
            </Card>

            <Card className="p-5">
              <h2 className="font-display text-lg font-bold">{t('reports.byPriority')}</h2>
              <BarList
                className="mt-3"
                items={bars(
                  data.byPriority,
                  (key) => t(`inbox.priority.${key}`),
                  (key) => `/inbox?priority=${key}`,
                )}
                format={(value) => formatNumberIn(value, locale)}
              />
            </Card>
          </div>

          <div>
            <h2 className="mb-4 font-display text-xl font-bold">{t('reports.perAgent')}</h2>
            <DataTable
              labels={tableLabels}
              columns={columns}
              // The name travels on the row for the same reason a message
              // snapshots its author's — but a colleague who has since been
              // deactivated is still looked up, so the spelling is one.
              data={data.perAgent.map((agent) => ({
                ...agent,
                fullName:
                  staff.data?.items.find((member) => member.id === agent.staffId)?.fullName ??
                  agent.fullName,
              }))}
              paginate={false}
              emptyMessage={t('reports.empty')}
            />
          </div>
        </div>
      )}
    </>
  );
}
